import { Injectable } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { ChangeType, EntityCollectionServiceElementsFactory } from '@ngrx/data';
import { concatLatestFrom } from '@ngrx/operators';
import { DateTime } from 'luxon';
import { PlEntityCollectionService } from 'pl-core-services';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  switchMap,
} from 'rxjs';
import { Client, Contact, Goal, IEP } from '../models';
import {
  PlAddClientEntity,
  PlDeleteClientEntity,
  PlDeleteClientObjective,
  PlDeleteSessionObjective,
  PlEditClientEntity,
  PlFetchClientEntities,
  PlFetchClientSessions,
} from './clients';
import { PlEventService } from './pl-event.service';

@UntilDestroy()
@Injectable()
export class PlClientService extends PlEntityCollectionService<Client> {
  private currentClientUuid$: BehaviorSubject<string> = new BehaviorSubject(
    null
  );

  private selectedIEPId$ = new BehaviorSubject<string>(null);

  get currentClientUuid() {
    return this.currentClientUuid$.value;
  }

  get selectedIEPId() {
    return this.selectedIEPId$.value;
  }

  set selectedIEPId(id: string) {
    this.selectedIEPId$.next(id);
  }

  readonly sortFn = (a: Client, b: Client) =>
    this.getName(a).localeCompare(this.getName(b));

  constructor(
    serviceElementsFactory: EntityCollectionServiceElementsFactory,
    eventService: PlEventService
  ) {
    super('Client', serviceElementsFactory);
    this.changeState$.pipe(untilDestroyed(this)).subscribe((state) => {
      const uuids = Object.keys(state);
      const clientHasBeenDeleted = uuids.find(
        (uuid) => state[uuid].changeType === ChangeType.Deleted
      );
      if (clientHasBeenDeleted) {
        // Clear events
        eventService.clearCache();
      }
    });
    this.selectCurrentIEPId()
      .pipe(
        filter((id) => !!id),
        untilDestroyed(this)
      )
      .subscribe((currentIEP) => {
        this.selectedIEPId = currentIEP;
      });

    combineLatest([this.selectCurrentClientId(), this.selectSelectedIEPId()])
      .pipe(
        filter(([clientId, iepId]) => !!clientId && !!iepId),
        untilDestroyed(this)
      )
      .subscribe(([clientId, iepId]) => {
        this.getGoals(clientId, iepId);
      });
  }

  clients$ = this.entities$.pipe(map((clients) => clients.sort(this.sortFn)));

  clientOptions$ = this.clients$.pipe(
    map((clients) =>
      clients.map((c) => ({
        value: c.uuid,
        label: `${c.first_name} ${c.last_name}`,
      }))
    )
  );

  getName(client: Client) {
    return `${client.first_name} ${client.last_name}`;
  }

  selectCurrentClient() {
    return this.selectCurrentClientId().pipe(
      switchMap((uuid) => this.selectEntityById(uuid))
    );
  }

  selectCurrentClientId() {
    return this.currentClientUuid$.pipe(distinctUntilChanged());
  }

  selectCurrentIEPId() {
    return this.selectCurrentClientIEPs().pipe(
      map((ieps) => ieps?.[0]?.uuid),
      distinctUntilChanged()
    );
  }

  selectCurrentClientIEPs() {
    return this.selectCurrentClient().pipe(
      map((client) => client?.ieps),
      distinctUntilChanged()
    );
  }

  selectIsCurrentIEPSelected() {
    return this.selectedIEPId$.pipe(
      switchMap((iepId) =>
        this.selectCurrentIEPId().pipe(map((currentId) => currentId === iepId))
      ),
      distinctUntilChanged()
    );
  }

  selectSelectedIEP() {
    return this.selectSelectedIEPId().pipe(
      concatLatestFrom(() => this.selectCurrentClientIEPs()),
      map(([selectedId, ieps]) => ieps?.find((iep) => iep.uuid === selectedId))
    );
  }

  selectSelectedIEPId() {
    return this.selectedIEPId$.asObservable().pipe(distinctUntilChanged());
  }

  setCurrentClient(uuid: string): Observable<Client> {
    this.currentClientUuid$.next(uuid);
    return this.selectCurrentClient();
  }

  // #region IEP

  addIEP(clientUuid: string, iep: IEP) {
    this.dispatch(
      PlAddClientEntity({ entity_name: 'ieps', clientUuid, data: iep })
    );
  }

  editIEP(clientUuid: string, iep: IEP) {
    this.dispatch(
      PlEditClientEntity({ entity_name: 'ieps', clientUuid, data: iep })
    );
  }

  // #endregion

  // #region Contacts
  selectCurrentContacts() {
    return this.selectCurrentClient().pipe(map(({ contacts }) => contacts));
  }

  getContacts(clientUuid: string) {
    this.dispatch(
      PlFetchClientEntities({ entity_name: 'contacts', clientUuid })
    );
  }

  addContact(clientUuid: string, contact: Contact) {
    this.dispatch(
      PlAddClientEntity({ entity_name: 'contacts', clientUuid, data: contact })
    );
  }

  editContact(clientUuid: string, contact: Contact) {
    this.dispatch(
      PlEditClientEntity({ entity_name: 'contacts', clientUuid, data: contact })
    );
  }

  deleteContact(clientUuid: string, contactUuid: string) {
    this.dispatch(
      PlDeleteClientEntity({
        entity_name: 'contacts',
        clientUuid,
        data: contactUuid,
      })
    );
  }
  // #endregion

  // #region Goals
  selectCurrentGoals() {
    return this.selectCurrentClient().pipe(map(({ goals }) => goals));
  }

  getGoals(clientUuid: string, iepId?: string) {
    this.dispatch(
      PlFetchClientEntities({
        entity_name: 'goals',
        clientUuid,
        data: { iep__uuid: iepId },
      })
    );
  }

  addGoal(clientUuid: string, goal: Goal) {
    this.dispatch(
      PlAddClientEntity({ entity_name: 'goals', clientUuid, data: goal })
    );
  }

  editGoal(clientUuid: string, goal: Goal) {
    this.dispatch(
      PlEditClientEntity({ entity_name: 'goals', clientUuid, data: goal })
    );
  }

  deleteGoal(clientUuid: string, goalUuid: string) {
    this.dispatch(
      PlDeleteClientEntity({ entity_name: 'goals', clientUuid, data: goalUuid })
    );
  }
  // #endregion

  // #region Objectives
  deleteObjective(clientUuid: string, objectiveUuid: string) {
    this.dispatch(PlDeleteClientObjective({ clientUuid, objectiveUuid }));
  }
  // #endregion

  // #region Sessions
  selectCurrentSessions() {
    return this.selectCurrentClient().pipe(map(({ sessions }) => sessions));
  }

  selectCurrentEventSession(eventUuid: string) {
    return this.selectCurrentSessions().pipe(
      map((sessions) => sessions.find((s) => s.event_uuid === eventUuid))
    );
  }

  clearSessions(clientUuid: string) {
    this.updateOneInCache({
      sessions: [],
      goals_loaded: false,
      goals: [],
      sessions_loaded: false,
      uuid: clientUuid,
    });
  }

  getSessions(
    clientUuid: string,
    sessionsFilter: { start: DateTime; end: DateTime; iepId?: string }
  ) {
    this.dispatch(PlFetchClientSessions({ clientUuid, ...sessionsFilter }));
  }

  deleteSessionObjective(
    clientUuid: string,
    eventUuid: string,
    sessionUuid: string,
    sessionObjectiveUuid: string
  ) {
    this.dispatch(
      PlDeleteSessionObjective({
        clientUuid,
        sessionUuid,
        eventUuid,
        sessionObjectiveUuid,
      })
    );
  }
  // #endregion
}
